/**
 * Horizontal Grouped Bar Chart Type Definition
 */

import {
  paletteControl,
  numCategoriesControl,
  numSeriesControl,
  barOrderingControl,
  gridControl,
  showValuesControl,
  valueDecimalsControl,
  axisControlsHorizontal,
  referenceLineControls,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  gridCode,
  axisLabelsCode,
  xAxisRangeCode,
  referenceLineCode,
  spineCode,
  finishCode,
} from '../shared/codeSnippets';

import {
  seabornSetup,
  seabornCreateFigure,
  seabornTitleCode,
  seabornGridCode,
  seabornAxisLabelsCode,
  seabornXAxisRangeCode,
  seabornReferenceLineCode,
  seabornSpineCode,
  seabornFinishCode,
} from '../shared/seabornSnippets';

import { getColors } from '../shared/palettes';
import { SERIES_HATCHES_MPL } from '../../utils/accessibility';

const showLegendControl = {
  key: 'showLegend',
  label: 'Show Legend',
  type: 'toggle',
};

const horizontalGroupedBar = {
  id: 'horizontal-grouped-bar',
  name: 'Horizontal Grouped Bar Chart',
  category: 'bar',
  description: 'Compare multiple metrics across categories with horizontal bars',

  sampleData: [
    { name: 'Group 1', series1: 78.2, series2: 79.5, series3: 77.8 },
    { name: 'Group 2', series1: 85.3, series2: 86.1, series3: 84.9 },
    { name: 'Group 3', series1: 88.7, series2: 89.2, series3: 88.3 },
    { name: 'Group 4', series1: 90.5, series2: 91.1, series3: 90.2 },
  ],

  defaultConfig: {
    externalColorMode: 'palette',
    useBluesPalette: true,
    useRedsPalette: false,
    useGreensPalette: false,
    numCategories: 4,
    numSeries: 3,
    outputFormat: 'matplotlib',
    showGrid: true,
    showValues: false,
    valueDecimals: 1,
    ylabel: 'Category',
    xlabel: 'Value',
    title: '',
    xMin: '',
    xMax: '',
    showReferenceLine: false,
    referenceValue: '',
    referenceLabel: 'Baseline',
    barOrdering: 'original',
    usePatternFills: false,
    showLegend: true,
    colorOverrides: undefined,
  },

  controls: [
    paletteControl,
    numCategoriesControl,
    numSeriesControl,
    barOrderingControl,
    gridControl,
    showValuesControl,
    valueDecimalsControl,
    {
      key: 'usePatternFills',
      label: 'Use Pattern Fills',
      type: 'toggle',
    },
    showLegendControl,
    ...axisControlsHorizontal,
    ...referenceLineControls,
  ],

  generateCode: (config, dataContext, s) => {
    const hasWideUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.inputShape === 'wide' && Array.isArray(dataContext.mapping?.valueColumns) && dataContext.mapping.valueColumns.length > 0;
    const hasLongUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.value && dataContext.mapping?.series;
    const hasUserData = hasWideUserData || hasLongUserData;
    const hasXMin = config.xMin && config.xMin.trim() !== '';
    const hasXMax = config.xMax && config.xMax.trim() !== '';
    const numCategories = config.numCategories || 4;
    const numSeries = config.numSeries || 3;
    const colors = getColors(config, hasWideUserData ? dataContext.mapping.valueColumns.length : numSeries, s);
    const hatches = JSON.stringify(SERIES_HATCHES_MPL);
    const usePatternFills = !!config.usePatternFills;
    const categories = Array.from({ length: numCategories }, (_, i) => `Group ${i + 1}`);

    if (config.outputFormat === 'seaborn') {
      const dataRows = [];
      for (let i = 0; i < numCategories; i++) {
        for (let j = 0; j < numSeries; j++) {
          dataRows.push(`    {'group': 'Group ${i + 1}', 'series': 'Series ${j + 1}', 'value': ${(75 + Math.random() * 20).toFixed(1)}}`);
        }
      }
      const orderCode = config.barOrdering === 'ascending'
        ? `\ngroup_order = df.groupby('group')['value'].mean().sort_values(ascending=True).index.tolist()\ndf['group'] = pd.Categorical(df['group'], categories=group_order, ordered=True)\ndf = df.sort_values('group')`
        : config.barOrdering === 'descending'
          ? `\ngroup_order = df.groupby('group')['value'].mean().sort_values(ascending=False).index.tolist()\ndf['group'] = pd.Categorical(df['group'], categories=group_order, ordered=True)\ndf = df.sort_values('group')`
          : '';
      const orderArg = config.barOrdering === 'original' ? '' : ', order=group_order';

      return `${seabornSetup(s)}

# ======== DATA ========
${hasUserData ? (hasWideUserData
  ? `df = pd.read_csv('${dataContext.fileName}')
df = df.melt(id_vars=['${dataContext.mapping.category}'], value_vars=${JSON.stringify(dataContext.mapping.valueColumns)},
             var_name='series', value_name='value')
df = df.rename(columns={'${dataContext.mapping.category}': 'group'})`
  : `df = pd.read_csv('${dataContext.fileName}')
df = df.rename(columns={'${dataContext.mapping.category}': 'group', '${dataContext.mapping.series}': 'series', '${dataContext.mapping.value}': 'value'})`) : `df = pd.DataFrame([
${dataRows.join(',\n')}
])`}${orderCode}
# ======================
${seabornCreateFigure(5.5, 4.5, 300, s)}

palette = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}

sns.barplot(data=df, x='value', y='group', hue='series', palette=palette${orderArg},
            edgecolor='${s?.axisColor || '#000000'}', linewidth=1.0, orient='h', ax=ax)
${config.usePatternFills ? `for i, container in enumerate(ax.containers):
    for bar in container:
        bar.set_hatch(hatch_patterns[i % len(hatch_patterns)])` : '# Pattern fills disabled'}
${seabornTitleCode(config, s)}
${seabornAxisLabelsCode(config, s)}

${seabornGridCode(config, 'x', s)}

${config.showValues ? `for container in ax.containers:
    ax.bar_label(container, fmt='%.${config.valueDecimals || 1}f', fontsize=${s?.valueLabelSize || 12}, color='${s?.foreground || '#000000'}', padding=3)` : '# Value labels disabled'}

${seabornXAxisRangeCode(config)}
${config.showLegend ? `ax.legend(title='', loc='upper right', fontsize=${s?.legendSize || 10})` : `ax.legend_.remove()`}
${seabornReferenceLineCode(config, 'vertical', s)}
${seabornSpineCode(s, 'number', 'label')}
${seabornFinishCode('horizontal_grouped_bar_chart')}`;
    }

    const seriesCode = Array.from({ length: numSeries }, (_, i) => {
      const values = Array.from({ length: numCategories }, () => (75 + Math.random() * 20).toFixed(1));
      return `series${i + 1} = [${values.join(', ')}]`;
    }).join('\n');
    const height = (0.8 / numSeries).toFixed(2);
    const barCodes = Array.from({ length: numSeries }, (_, i) => {
      const offset = ((i - (numSeries - 1) / 2) * parseFloat(height)).toFixed(2);
      return `bars${i + 1} = ax.barh(y + ${offset}, series${i + 1}, ${height}, label='Series ${i + 1}', color=colors[${i}], hatch=(hatch_patterns[${i} % len(hatch_patterns)] if ${usePatternFills ? 'True' : 'False'} else ''))`;
    }).join('\n');
    const sampleOrderingCode = config.barOrdering === 'ascending'
      ? `group_means = np.mean(np.array([${Array.from({ length: numSeries }, (_, i) => `series${i + 1}`).join(', ')}]), axis=0)
sorted_indices = np.argsort(group_means)
categories = [categories[i] for i in sorted_indices]
${Array.from({ length: numSeries }, (_, i) => `series${i + 1} = [series${i + 1}[i] for i in sorted_indices]`).join('\n')}`
      : config.barOrdering === 'descending'
        ? `group_means = np.mean(np.array([${Array.from({ length: numSeries }, (_, i) => `series${i + 1}`).join(', ')}]), axis=0)
sorted_indices = np.argsort(group_means)[::-1]
categories = [categories[i] for i in sorted_indices]
${Array.from({ length: numSeries }, (_, i) => `series${i + 1} = [series${i + 1}[i] for i in sorted_indices]`).join('\n')}`
        : '# Keep original category ordering';

    if (hasUserData) {
      return `${matplotlibSetup(s)}

# ======== DATA ========
df = pd.read_csv('${dataContext.fileName}')
${hasWideUserData
  ? `pivot = df.set_index('${dataContext.mapping.category}')[${JSON.stringify(dataContext.mapping.valueColumns)}]`
  : `pivot = df.pivot_table(index='${dataContext.mapping.category}', columns='${dataContext.mapping.series}', values='${dataContext.mapping.value}', aggfunc='first')`}
${config.barOrdering === 'ascending'
  ? `pivot = pivot.loc[pivot.mean(axis=1).sort_values(ascending=True).index]`
  : config.barOrdering === 'descending'
    ? `pivot = pivot.loc[pivot.mean(axis=1).sort_values(ascending=False).index]`
    : '# Keep original group ordering'}
categories = pivot.index.tolist()
series_names = pivot.columns.tolist()
# ======================
${createFigure(5.5, 4.5, 300, s)}

colors = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}
y = np.arange(len(categories))
height = 0.8 / len(series_names)

for idx, series_name in enumerate(series_names):
    offset = (idx - (len(series_names) - 1) / 2) * height
    vals = pivot[series_name].values
    ax.barh(y + offset, vals, height, label=series_name, color=colors[idx % len(colors)],
            hatch=(hatch_patterns[idx % len(hatch_patterns)] if ${usePatternFills ? 'True' : 'False'} else ''))

ax.set_yticks(y)
ax.set_yticklabels(categories)
${config.showValues ? `for container in ax.containers:
    ax.bar_label(container, fmt='%.${config.valueDecimals || 1}f', fontsize=${s?.valueLabelSize || 12}, padding=3)` : '# Value labels disabled'}
${titleCode(config, s)}
${axisLabelsCode(config, s)}
${gridCode(config, 'x', s)}
${hasXMin || hasXMax ? `ax.set_xlim(${hasXMin ? `left=${config.xMin}` : ''}${hasXMin && hasXMax ? ', ' : ''}${hasXMax ? `right=${config.xMax}` : ''})` : '# Auto x-axis range'}
${config.showLegend ? `ax.legend(loc='upper right', fontsize=${s?.legendSize || 10})` : '# Legend disabled'}
${referenceLineCode(config, 'vertical', s)}
${spineCode(s, 'number', 'label')}
${finishCode('horizontal_grouped_bar_chart')}`;
    }

    return `${matplotlibSetup(s)}

# ======== DATA ========
categories = ${JSON.stringify(categories)}
${seriesCode}
# ======================
${sampleOrderingCode}
${createFigure(5.5, 4.5, 300, s)}

colors = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}
y = np.arange(len(categories))

${barCodes}
ax.set_yticks(y)
ax.set_yticklabels(categories)
${config.showValues ? `for container in [${Array.from({ length: numSeries }, (_, i) => `bars${i + 1}`).join(', ')}]:
    ax.bar_label(container, fmt='%.${config.valueDecimals || 1}f', fontsize=${s?.valueLabelSize || 12}, padding=3)` : '# Value labels disabled'}
${titleCode(config, s)}
${axisLabelsCode(config, s)}
${gridCode(config, 'x', s)}
${xAxisRangeCode(config)}
${config.showLegend ? `ax.legend(loc='upper right', fontsize=${s?.legendSize || 10})` : '# Legend disabled'}
${referenceLineCode(config, 'vertical', s)}
${spineCode(s, 'number', 'label')}
${finishCode('horizontal_grouped_bar_chart')}`;
  },
};

export default horizontalGroupedBar;
